import { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { PrimitiveManagedTable } from "@/lib/form/layout";
import { GeneralContract, Location, MarketZone } from "@/lib/api/generalContract";
import { Trader, Customer } from "@/lib/api/reports";
import {
  BaseFormSchema,
  StagedFormSchema,
} from "../shared/GeneralContractSchemas";
import {
  GeneralContractModals,
  GeneralContractModalsProps,
} from "../shared/GeneralContractModals";
import { useGeneralContractHandlers } from "../shared/useGeneralContractHandlers";

interface GeneralContractContentProps {
  showTitle?: boolean;
  contracts: GeneralContract[];
  stagedContracts: GeneralContract[];
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
}

export function GeneralContractContent({
  showTitle = true,
  contracts,
  stagedContracts,
  locations,
  marketZones,
  customers,
  traders,
}: GeneralContractContentProps) {
  const [tabValue, setTabValue] = useState("unstaged");
  const {
    isArchiving,
    isReviewing,
    reviewModalId,
    setReviewModalId,
    isCreateOpen,
    isUpdateOpen,
    onArchive,
    onDuplicate,
    onDelete,
    onReview,
    confirmReviewPrompt,
    createPopulateWith,
    updatePopulateWith,
    setIsCreateOpen,
    setIsUpdateOpen,
  } = useGeneralContractHandlers();

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      {showTitle && (
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          General Contracts
        </h1>
      )}
      <div className="flex justify-end gap-4 mb-4">
        <GeneralContractModals
          locations={locations}
          marketZones={marketZones}
          customers={customers}
          traders={traders}
          onDuplicate={onDuplicate}
          onArchive={onArchive}
          onDelete={onDelete}
          onReview={onReview}
          isArchiving={isArchiving}
          isReviewing={isReviewing}
          reviewModalId={reviewModalId}
          setReviewModalId={setReviewModalId}
          createPopulateWith={createPopulateWith}
          updatePopulateWith={updatePopulateWith}
          isCreateOpen={isCreateOpen}
          setIsCreateOpen={setIsCreateOpen}
          isUpdateOpen={isUpdateOpen}
          setIsUpdateOpen={setIsUpdateOpen}
        />
      </div>

      <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
        <TabsList>
          <TabsTrigger value="unstaged">Contracts</TabsTrigger>
          <TabsTrigger value="staged">Review</TabsTrigger>
        </TabsList>

        <TabsContent value="unstaged">
          <PrimitiveManagedTable
            tableHeight="600px"
            externalReferences={{
              onDuplicate: onDuplicate,
              onArchive: onArchive,
              onArchiveLoading: isArchiving,
            }}
            schema={BaseFormSchema}
            values={contracts}
            onRowClick={(item) => {
              setIsUpdateOpen(true);
              updatePopulateWith(item);
            }}
          />
        </TabsContent>
        <TabsContent value="staged">
          <PrimitiveManagedTable
            tableHeight="600px"
            externalReferences={{
              onDuplicate: onDuplicate,
              onReview: confirmReviewPrompt,
              onReviewLoading: isReviewing,
              onDelete: onDelete,
              onDeleteLoading: isArchiving,
            }}
            schema={StagedFormSchema}
            values={stagedContracts}
            onRowClick={(item) => {
              setIsUpdateOpen(true);
              updatePopulateWith(item);
            }}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

